import { createContext, useContext, ReactNode } from "react";
import type { ISummaryData } from "@/types/summary";
import type { IJob } from "@/hooks/useJobs";
import type { Collaborator } from "@/components/Sharing/types";

export interface JobDetailContextType {
  summaryData: ISummaryData | null;
  isLoading: boolean;
  job: IJob | null;
  collaborators: Collaborator[];
  ownerEmail: string;
  addCollaborators: (emails: string[]) => Promise<void>;
  removeCollaborator: (email: string) => Promise<void>;
}

const JobDetailContext = createContext<JobDetailContextType | undefined>(
  undefined,
);

export function JobDetailProvider({
  children,
  summaryData,
  isLoading,
  job,
  collaborators,
  ownerEmail,
  addCollaborators,
  removeCollaborator,
}: {
  children: ReactNode;
  summaryData: ISummaryData | null;
  isLoading: boolean;
  job: IJob | null;
  collaborators: Collaborator[];
  ownerEmail: string;
  addCollaborators: (emails: string[]) => Promise<void>;
  removeCollaborator: (email: string) => Promise<void>;
}) {
  return (
    <JobDetailContext.Provider
      value={{
        summaryData,
        isLoading,
        job,
        collaborators,
        ownerEmail,
        addCollaborators,
        removeCollaborator,
      }}
    >
      {children}
    </JobDetailContext.Provider>
  );
}

export function useJobDetail() {
  const context = useContext(JobDetailContext);
  if (context === undefined) {
    throw new Error("useJobDetail must be used within a JobDetailProvider");
  }
  return context;
}
